/**
 * Weekly Deliverables shared domain rules.
 *
 * This module is dependency-free on purpose: server routes and client views
 * must derive status from the same frozen ads + reviewer decisions rather than
 * trusting mutable status copies.
 */
export const WEEKLY_DELIVERABLES_FLAG_KEY = "weekly_deliverables";

export const DELIVERABLE_REJECT_REASONS = [
  { key: "wrong_offer", label: "Wrong offer or price" },
  { key: "copy_off", label: "Copy / messaging is off" },
  { key: "off_brand", label: "Doesn't look like our brand" },
  { key: "wrong_product", label: "Wrong product or feature shown" },
  { key: "image_broken", label: "Image looks wrong or broken" },
  { key: "text_illegible", label: "Text is hard to read" },
  { key: "legal", label: "Legal / claims problem" },
  { key: "wrong_format", label: "Wrong size or format" },
  { key: "other", label: "Something else" },
] as const;

export const DELIVERABLE_POSITIVE_TAGS = [
  { key: "on_brand", label: "On brand" },
  { key: "strong_hook", label: "Strong hook" },
  { key: "right_offer", label: "Right offer" },
  { key: "use_as_reference", label: "Use this as a reference" },
] as const;

export type DeliverableRejectReasonKey =
  (typeof DELIVERABLE_REJECT_REASONS)[number]["key"];
export type DeliverablePositiveTagKey =
  (typeof DELIVERABLE_POSITIVE_TAGS)[number]["key"];
export type DeliverableDisposition = "rework" | "drop";
export type AdDecisionValue = "approved" | "rejected";
export type DeliverableStatus =
  | "to_review"
  | "in_review"
  | "approved"
  | "changes_requested"
  | "rejected";
export type DeliveryStatus =
  | "draft"
  | "delivered"
  | "in_review"
  | "complete"
  | "closed"
  | "revoked";

export type DeliverableDecisionLike = {
  deliveryItemAdId?: string | null;
  itemAdId?: string | null;
  adId?: string | null;
  decision: AdDecisionValue;
  reasons?: readonly string[] | null;
  comment?: string | null;
  disposition?: DeliverableDisposition | null;
  [key: string]: unknown;
};

function decisionAdKey(decision: DeliverableDecisionLike): string {
  return String(
    decision.deliveryItemAdId ??
      decision.itemAdId ??
      decision.adId ??
      "",
  );
}

export function resolveAdDecisionForAd<T extends DeliverableDecisionLike>(
  decisions: readonly T[],
): {
  decision: AdDecisionValue | null;
  decisions: T[];
  winningDecision: T | null;
} {
  const preserved = [...decisions];
  const rejection = preserved.find((decision) => decision.decision === "rejected") ?? null;
  if (rejection) {
    return { decision: "rejected", decisions: preserved, winningDecision: rejection };
  }
  const approval = preserved.find((decision) => decision.decision === "approved") ?? null;
  return {
    decision: approval ? "approved" : null,
    decisions: preserved,
    winningDecision: approval,
  };
}

export function resolvedDecisionsByAd(
  decisions: readonly DeliverableDecisionLike[],
): Map<string, ReturnType<typeof resolveAdDecisionForAd>> {
  const grouped = new Map<string, DeliverableDecisionLike[]>();
  for (const decision of decisions) {
    const key = decisionAdKey(decision);
    if (!key) continue;
    grouped.set(key, [...(grouped.get(key) ?? []), decision]);
  }
  return new Map(
    [...grouped.entries()].map(([key, rows]) => [
      key,
      resolveAdDecisionForAd(rows),
    ]),
  );
}

export function summarizeDeliverable(args: {
  adCount: number;
  decisions: readonly DeliverableDecisionLike[];
}): {
  approved: number;
  rejected: number;
  unanswered: number;
  reworkCount: number;
  dropCount: number;
  display: string;
  summaryLabel: string;
} {
  const resolved = resolvedDecisionsByAd(args.decisions);
  let approved = 0;
  let rejected = 0;
  let reworkCount = 0;
  let dropCount = 0;
  for (const result of resolved.values()) {
    if (result.decision === "approved") approved += 1;
    if (result.decision === "rejected") {
      rejected += 1;
      if (result.winningDecision?.disposition === "drop") dropCount += 1;
      else reworkCount += 1;
    }
  }
  const unanswered = Math.max(0, args.adCount - approved - rejected);
  const display = `${approved} of ${args.adCount} approved`;
  return {
    approved,
    rejected,
    unanswered,
    reworkCount,
    dropCount,
    display,
    summaryLabel: display,
  };
}

export function deriveDeliverableStatus(args: {
  adCount: number;
  decisions: readonly DeliverableDecisionLike[];
  submittedAt?: Date | string | null;
  projectRejectedAt?: Date | string | null;
}): DeliverableStatus {
  if (args.projectRejectedAt) return "rejected";
  const summary = summarizeDeliverable(args);
  if (args.submittedAt) {
    if (summary.rejected > 0) return "changes_requested";
    if (args.adCount > 0 && summary.unanswered === 0) return "approved";
    return "in_review";
  }
  return summary.approved + summary.rejected > 0 ? "in_review" : "to_review";
}

export function deriveDeliveryStatus(args: {
  storedStatus: "draft" | "delivered" | "closed" | "revoked" | string;
  items: readonly (
    | DeliverableStatus
    | {
        status?: DeliverableStatus;
        adCount?: number;
        decisions?: readonly DeliverableDecisionLike[];
        submittedAt?: Date | string | null;
        projectRejectedAt?: Date | string | null;
      }
  )[];
}): DeliveryStatus {
  if (args.storedStatus === "draft") return "draft";
  if (args.storedStatus === "closed") return "closed";
  if (args.storedStatus === "revoked") return "revoked";
  const statuses = args.items.map((item) =>
    typeof item === "string"
      ? item
      : item.status ??
        deriveDeliverableStatus({
          adCount: item.adCount ?? 0,
          decisions: item.decisions ?? [],
          submittedAt: item.submittedAt,
          projectRejectedAt: item.projectRejectedAt,
        }),
  );
  if (
    statuses.length > 0 &&
    statuses.every(
      (status) =>
        status === "approved" ||
        status === "changes_requested" ||
        status === "rejected",
    )
  ) {
    return "complete";
  }
  if (
    statuses.some(
      (status) =>
        status === "in_review" ||
        status === "approved" ||
        status === "changes_requested" ||
        status === "rejected",
    )
  ) {
    return "in_review";
  }
  return "delivered";
}

export function validateDeliverableSubmit(args: {
  ads: readonly ({ id?: string; itemAdId?: string } | string)[];
  decisions: readonly DeliverableDecisionLike[];
  allowUnanswered?: boolean;
}): {
  ok: boolean;
  blockedAdIds: string[];
  unansweredAdIds: string[];
  message: string;
} {
  const adIds = args.ads.map((ad) =>
    typeof ad === "string" ? ad : String(ad.itemAdId ?? ad.id ?? ""),
  );
  const grouped = new Map<string, DeliverableDecisionLike[]>();
  for (const decision of args.decisions) {
    const key = decisionAdKey(decision);
    grouped.set(key, [...(grouped.get(key) ?? []), decision]);
  }
  const blockedAdIds: string[] = [];
  const unansweredAdIds: string[] = [];
  for (const adId of adIds) {
    const rows = grouped.get(adId) ?? [];
    const resolved = resolveAdDecisionForAd(rows);
    if (!resolved.decision) {
      unansweredAdIds.push(adId);
      continue;
    }
    const incompleteRejection = rows.some(
      (decision) =>
        decision.decision === "rejected" &&
        (!(decision.reasons?.length) || !decision.comment?.trim()),
    );
    if (incompleteRejection) blockedAdIds.push(adId);
  }
  if (blockedAdIds.length) {
    return {
      ok: false,
      blockedAdIds,
      unansweredAdIds,
      message: "Every rejection needs at least one reason and a comment.",
    };
  }
  if (unansweredAdIds.length && !args.allowUnanswered) {
    return {
      ok: false,
      blockedAdIds: [],
      unansweredAdIds,
      message: `${unansweredAdIds.length} ad${unansweredAdIds.length === 1 ? " has" : "s have"} no answer yet.`,
    };
  }
  return { ok: true, blockedAdIds: [], unansweredAdIds, message: "" };
}

export type DeliverableReviewAd = {
  itemAdId: string;
  adId: string | null;
  displayName: string;
  aspectRatio: string | null;
  imageUrl: string | null;
  sortOrder: number;
  removed: boolean;
};

export type DeliverableAdImageSource =
  | { kind: "objectKey"; value: string }
  | { kind: "originalUrl"; value: string };

// Keep in lockstep with the explicit BTRIM character set in
// server/deliverables-routes.ts. Object keys and URLs are ASCII transport
// values, so this rejects every ASCII whitespace edge without rewriting a
// potentially valid value in the middle.
const DELIVERY_IMAGE_EDGE_WHITESPACE = /^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g;

function trimDeliverableImageValue(value: string | null | undefined): string | null {
  const trimmed = value?.replace(DELIVERY_IMAGE_EDGE_WHITESPACE, "");
  return trimmed || null;
}

/**
 * Selects the source that will be frozen into a delivery snapshot. Whitespace
 * is never a usable object key or URL, and a blank key must not mask a valid
 * original URL.
 */
export function selectDeliverableAdImageSource(ad: {
  imageObjectKey?: string | null;
  imageUrlOriginal?: string | null;
}): DeliverableAdImageSource | null {
  const objectKey = trimDeliverableImageValue(ad.imageObjectKey);
  if (objectKey) return { kind: "objectKey", value: objectKey };
  const originalUrl = trimDeliverableImageValue(ad.imageUrlOriginal);
  if (originalUrl) return { kind: "originalUrl", value: originalUrl };
  return null;
}

export function serializeDeliverableReviewAd(row: {
  id: string;
  adId?: string | null;
  displayNameSnapshot?: string | null;
  aspectRatioSnapshot?: string | null;
  imageUrlSnapshot?: string | null;
  sortOrder?: number | null;
}): DeliverableReviewAd {
  return {
    itemAdId: row.id,
    adId: row.adId ?? null,
    displayName: row.displayNameSnapshot?.trim() || "Untitled ad",
    aspectRatio: row.aspectRatioSnapshot ?? null,
    imageUrl: row.imageUrlSnapshot ?? null,
    sortOrder: row.sortOrder ?? 0,
    removed: !row.adId,
  };
}

export type DeliverableReviewBadgeStatus =
  | "approved-current"
  | "approved-earlier"
  | "rejected-current"
  | "rejected-earlier";

/**
 * Finds the newest explicit review decision across frozen delivery snapshots.
 * An untouched newer snapshot must not hide an earlier client decision, while
 * a newer approved or rejected snapshot always supersedes prior feedback.
 */
export function resolveReviewBadgeFromSnapshots(args: {
  currentImageUrl: string | null;
  snapshotsNewestFirst: readonly {
    imageUrlSnapshot: string | null;
    decisions: readonly DeliverableDecisionLike[];
  }[];
}): DeliverableReviewBadgeStatus | null {
  for (const snapshot of args.snapshotsNewestFirst) {
    const resolved = resolveAdDecisionForAd(snapshot.decisions);
    if (resolved.decision) {
      const version = snapshot.imageUrlSnapshot === args.currentImageUrl
        ? "current"
        : "earlier";
      return `${resolved.decision}-${version}`;
    }
    // An unanswered newer snapshot does not erase an older explicit decision.
  }
  return null;
}

/** Backward-compatible approval-only view used by existing approval callers. */
export function resolveApprovalBadgeFromSnapshots(args: {
  currentImageUrl: string | null;
  snapshotsNewestFirst: readonly {
    imageUrlSnapshot: string | null;
    decisions: readonly DeliverableDecisionLike[];
  }[];
}): "current" | "earlier" | null {
  const status = resolveReviewBadgeFromSnapshots(args);
  if (status === "approved-current") return "current";
  if (status === "approved-earlier") return "earlier";
  return null;
}
